/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';

import { withAuthenticationRequired } from '@auth0/auth0-react';
import { isEqual } from 'lodash-es';

import { EvSpinner, PendoService } from '@evinced-private/ui-common';

import { DashboardSkeleton } from 'src/apps/dashboard/components/skeletons/dashboard-skeleton/DashboardSkeleton';
import {
	WebSdkTestIssuesSkeleton,
	WebSdkTestsSkeleton
	// eslint-disable-next-line boundaries/element-types
} from 'src/apps/web-sdks/components/skeletons';
// eslint-disable-next-line boundaries/element-types
import { WfaSessionOverviewSkeleton, WfaSessionsSkeleton } from 'src/apps/wfa/components/skeletons';
// eslint-disable-next-line boundaries/element-types
import { WfaSessionAllIssuesSkeleton } from 'src/apps/wfa/components/skeletons/wfa-session-components-all-issues-skeleton/WfaSessionAllIssuesSkeleton';
// eslint-disable-next-line boundaries/element-types
import { WfaSessionComponentsSkeleton } from 'src/apps/wfa/components/skeletons/wfa-session-components-skeleton/WfaSessionComponentsSkeleton';
import { getErrorMessage } from 'src/common/helpers/ApiErrorHelper';
import { isCI } from 'src/common/helpers/EnvironmentHelper';
import { getInvitationTokenFromUrl } from 'src/common/helpers/InvitationTokenHelper';
import { getProductTypeFromPath, RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { Unauthorized403Page } from 'src/common/pages/unauthorized-403-page/Unauthorized403Page';
import {
	getEvincedUserId,
	getUserEmail,
	getUserEmailDomain,
	isLoggedIn
} from 'src/common/services/AuthenticationService';
import { Logger } from 'src/common/services/Logger';
import { setRedirectToPath } from 'src/common/services/LoginRedirectionService';
import { getUserTenant, userHasAccessToProduct } from 'src/common/services/TenantsService';

import { getAllUserPendingInvitations } from '../../services/UserInvitationService';
import { FullPageError } from '../errors/full-page-error/FullPageError';

const pendoApiKey = import.meta.env.VITE_PENDO_API_KEY;

const getCurrentRelativeUrl = (): string => {
	let url = window.location.pathname;
	const params = window.location.search;
	const urlParams = new URLSearchParams(params);
	const searchParams = urlParams.toString();
	if (searchParams) {
		url += `?${searchParams}`;
	}
	return url;
};

const saveCurrentUrlForRedirection = async (): Promise<void> => {
	const redirectToUrl = getCurrentRelativeUrl();
	setRedirectToPath(redirectToUrl);
};
/**
 *
 * @returns {hasTenant: boolean, hasAccessToProduct: boolean,
 *  hasPendingInvitations: boolean }
 */
const getUserStatus = async (): Promise<{
	hasTenant: boolean;
	hasAccessToProduct: boolean;
	hasPendingInvitations: boolean;
}> => {
	const userTenant = await getUserTenant();
	const hasAccessToProduct = userTenant
		? await userHasAccessToProduct(productType, userTenant)
		: false;
	const invitaitonTokenFromUrl = getInvitationTokenFromUrl();
	const pendingInvitations = await getAllUserPendingInvitations();

	return {
		hasTenant: !!userTenant,
		hasAccessToProduct,
		hasPendingInvitations: !!invitaitonTokenFromUrl || pendingInvitations.length > 0
	};
};

const initializePendo = (): void => {
	if (!isCI()) {
		PendoService.init(
			{
				visitorId: getEvincedUserId(),
				email: getUserEmail(),
				accountId: getUserEmailDomain()
			},
			pendoApiKey,
			Logger
		);
	}
};

const productType = getProductTypeFromPath();

/**
 * Check if the current route is a dashboard route (starting with /dashboard)
 * this function is used to show the skeleton loading state for dashboard routes
 * as it is no implemented in the whole of the platform for now
 */
const isDashboardRoute = (): boolean => {
	const { pathname } = window.location;
	// Exclude the /dashboard/issues route from the skeleton for now
	if (pathname.startsWith(RoutesHelper.getDashboardIssuesPath())) {
		return false;
	}
	return pathname.startsWith(RoutesHelper.getAccessibilityDashboardPath());
};

const isWebSdkDashboardRoute = (): boolean => {
	const { pathname } = window.location;
	return pathname.startsWith(RoutesHelper.getWebSdkDashboardPath());
};

const isWebSdkTestDetailsRoute = (): boolean => {
	const { pathname } = window.location;
	return pathname.startsWith(RoutesHelper.getWebSdkDashboardTestPath());
};

const isWfaDashboardRoute = (): boolean => {
	const { pathname } = window.location;
	return pathname.startsWith(RoutesHelper.getWfaDashboardPath());
};

const isWfaSingleSessionOverviewRoute = (): boolean => {
	const { pathname } = window.location;

	return (
		pathname.startsWith(RoutesHelper.getWfaSessionBasePath()) && pathname.endsWith('/overview')
	);
};

const isWfaSingleSessionComponentsRoute = (): boolean => {
	const { pathname } = window.location;

	return (
		pathname.startsWith(RoutesHelper.getWfaSessionBasePath()) && pathname.endsWith('/components')
	);
};

const isWfaSingleSessionAllIssuesRoute = (): boolean => {
	const { pathname } = window.location;

	return (
		pathname.startsWith(RoutesHelper.getWfaSessionBasePath()) && pathname.endsWith('/all-issues')
	);
};

function PrivateRouteInner({ component: RouteComponent, ...rest }): JSX.Element {
	const [isLoading, setLoading] = useState(true);
	const [userStatus, setUserStatus] = useState({
		hasTenant: false,
		hasAccessToProduct: false,
		hasPendingInvitations: false
	});
	const [error, setError] = useState(null);
	const { location } = rest;

	const checkIfUserHasTenant = useCallback(async () => {
		// short circuit - no need to check if there's a tenant on each route if we know they have
		if (userStatus.hasTenant && userStatus.hasAccessToProduct) {
			return;
		}
		setLoading(true);

		try {
			const updatedUserStatus = await getUserStatus();
			if (!isEqual(updatedUserStatus, userStatus)) {
				setUserStatus(updatedUserStatus);
			}
		} catch (error) {
			Logger.error('Error getting tenant', error);
			setError(
				`An error occurred while retrieving your account information. ${getErrorMessage(error)}`
			);
		} finally {
			setLoading(false);
		}
	}, [userStatus]);

	useEffect(() => {
		checkIfUserHasTenant();
	}, [checkIfUserHasTenant]);

	if (isLoading) {
		// Show skeleton loading state for dashboard routes, spinner for other routes
		if (isDashboardRoute()) {
			return <DashboardSkeleton isAnimated={false} />;
		}
		if (isWfaSingleSessionOverviewRoute()) {
			return <WfaSessionOverviewSkeleton isAnimated={false} />;
		}
		if (isWfaSingleSessionComponentsRoute()) {
			return <WfaSessionComponentsSkeleton isAnimated={false} />;
		}
		if (isWfaSingleSessionAllIssuesRoute()) {
			return <WfaSessionAllIssuesSkeleton isAnimated={false} />;
		}
		if (isWfaDashboardRoute()) {
			return <WfaSessionsSkeleton isAnimated={false} />;
		}
		if (isWebSdkTestDetailsRoute()) {
			return <WebSdkTestIssuesSkeleton isAnimated={false} />;
		}
		if (isWebSdkDashboardRoute()) {
			return <WebSdkTestsSkeleton isAnimated={false} />;
		}
		return <EvSpinner />;
	}
	if (!isLoading && !userStatus.hasAccessToProduct) {
		return <Unauthorized403Page />;
	}
	if (error) {
		return <FullPageError title="Product Loading Error" />;
	}
	initializePendo();
	return (
		<Route
			{...rest}
			location={location}
			render={(props) => {
				if (!isLoggedIn()) {
					return (
						<Redirect
							to={{
								pathname: '/login',
								state: { from: props.location },
								search: props.location.search
							}}
						/>
					);
				}

				// handling account activation and product invitation
				const { pathname } = location;
				const { hasTenant, hasAccessToProduct } = userStatus;

				if (!hasTenant || !hasAccessToProduct) {
					let redirectTo = '';
					if (hasTenant && !hasAccessToProduct) {
						redirectTo = RoutesHelper.getMfaDashboardPath();
					}
					// no need for redirect if the user is already in that path
					if (pathname !== redirectTo) {
						return (
							<Redirect
								to={{
									pathname: redirectTo,
									state: { from: props.location },
									search: props.location.search
								}}
							/>
						);
					}
				}
				return <RouteComponent {...props} />;
			}}
		/>
	);
}

export const PrivateRoute = isCI()
	? PrivateRouteInner
	: withAuthenticationRequired(PrivateRouteInner, {
			onBeforeAuthentication: saveCurrentUrlForRedirection,
			onRedirecting: () => {
				// Show skeleton loading state for dashboard routes, spinner for other routes
				if (isDashboardRoute()) {
					return <DashboardSkeleton isAnimated={false} />;
				}
				if (isWfaSingleSessionOverviewRoute()) {
					return <WfaSessionOverviewSkeleton isAnimated={false} />;
				}
				if (isWfaSingleSessionComponentsRoute()) {
					return <WfaSessionComponentsSkeleton isAnimated={false} />;
				}
				if (isWfaSingleSessionAllIssuesRoute()) {
					return <WfaSessionAllIssuesSkeleton isAnimated={false} />;
				}
				if (isWfaDashboardRoute()) {
					return <WfaSessionsSkeleton isAnimated={false} />;
				}
				if (isWebSdkTestDetailsRoute()) {
					return <WebSdkTestIssuesSkeleton isAnimated={false} />;
				}
				if (isWebSdkDashboardRoute()) {
					return <WebSdkTestsSkeleton isAnimated={false} />;
				}
				return <EvSpinner />;
			}
		});
